import React, { useEffect, useRef, useState } from "react";
import Highcharts from "highcharts";
import { Container, Card } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";

// ─── Colors ───────────────────────────────────────────────────────────────────
const colors = {
  primary: "#8B83A6", // intersection bars
  primaryDark: "#6D6589", // hover
  textDark: "#2D2D4E",
  textMuted: "#8B83A6",
  border: "#AEB8CE",
  background: "#F5F6FA",
  dotEmpty: "#D8D9E3",
  line: "#8B83A6",
  white: "#FFFFFF",
  grid: "#E4E6EF",
};

// Darker variations of palette hues — blue, teal, green, coral, purple
const SET_COLORS = ["#6B8FD9", "#4FAFAF", "#5BAD7A", "#E8945C", "#9B72B5"];

const SETS = ["Aetna", "UHC", "Cigna", "Aultcare", "BCBS"];

const ALL_INTERSECTIONS = [
  { sets: [0, 1, 2], count: 1240 },
  { sets: [0, 2], count: 980 },
  { sets: [0, 1], count: 870 },
  { sets: [1, 2], count: 640 },
  { sets: [0], count: 520 },
  { sets: [0, 1, 2, 3], count: 480 },
  { sets: [2], count: 430 },
  { sets: [0, 2, 4], count: 310 },
  { sets: [1], count: 290 },
  { sets: [0, 4], count: 210 },
  { sets: [3], count: 180 },
  { sets: [2, 3], count: 150 },
  { sets: [0, 1, 4], count: 130 },
  { sets: [1, 3], count: 110 },
  { sets: [4], count: 90 },
];

const SET_TOTALS = SETS.map((_, si) =>
  ALL_INTERSECTIONS.filter((i) => i.sets.includes(si)).reduce(
    (a, i) => a + i.count,
    0,
  ),
);

// ─── Layout ───────────────────────────────────────────────────────────────────
const BAR_H = 340; // intersection bar chart height
const COL_W = 52; // dot-matrix column width
const ROW_H = 64; // dot-matrix row height
const DOT_R = 9; // dot radius
const LEFT_W = 200; // left panel width
const LABEL_H = 0; // no column labels
// Right-side y-axis for intersection chart (avoids overlapping bar data labels)
const SCALE_W = 38;

// ─── Shared tooltip base options ─────────────────────────────────────────────
const tooltipBase = {
  useHTML: true,
  backgroundColor: "white",
  borderColor: "#A9A9A9",
  borderRadius: 7,
  shared: true,
  style: { fontFamily: "'Inter', sans-serif", padding: "4px" },
};

// ─── Component ────────────────────────────────────────────────────────────────
const UpSetPlot = () => {
  const barChartRef = useRef(null);
  const setBarRef = useRef(null);
  const [hoveredCol, setHoveredCol] = useState(null);

  const intersections = ALL_INTERSECTIONS;
  const maxCount = Math.max(...intersections.map((d) => d.count), 1);

  const matrixW = intersections.length * COL_W;
  const matrixH = SETS.length * ROW_H;

  // Chart widths — bar chart gets extra SCALE_W on the right for the axis
  const chartW = Math.max(matrixW, 100) + SCALE_W;

  // ── Intersection bar chart ─────────────────────────────────────────────────
  useEffect(() => {
    if (!barChartRef.current) return;

    const data = intersections.map((inter) => ({ y: inter.count }));

    const chart = Highcharts.chart(barChartRef.current, {
      chart: {
        type: "column",
        width: chartW,
        height: BAR_H,
        backgroundColor: colors.white,
        style: { fontFamily: "'Inter', sans-serif" },
        margin: [72, SCALE_W, 0, 0],
        spacing: [0, 0, 0, 0],
        animation: { duration: 400 },
      },
      title: {
        text: "Intersection Size",
        align: "left",
        style: {
          fontSize: "13px",
          fontWeight: "700",
          color: "#2D2D4E",
          fontFamily: "'Inter', sans-serif",
        },
        margin: 10,
      },
      subtitle: {
        text: "Number of providers belonging to each payer combination",
        align: "left",
        style: {
          fontSize: "11px",
          color: "#8B83A6",
          fontFamily: "'Inter', sans-serif",
        },
      },
      xAxis: {
        categories: intersections.map((_, i) => `I${i + 1}`),
        labels: { enabled: false },
        lineWidth: 0,
        tickWidth: 0,
      },
      yAxis: {
        title: { text: null },
        opposite: true, // axis on right — no conflict with bars or data labels
        tickAmount: 5,
        labels: {
          x: 4,
          style: {
            fontSize: "9px",
            color: colors.textMuted,
            fontFamily: "'Inter', sans-serif",
          },
          formatter() {
            return this.value >= 1000
              ? (this.value / 1000).toFixed(0) + "k"
              : this.value;
          },
        },
        gridLineColor: colors.grid,
        gridLineWidth: 1,
        min: 0,
      },
      plotOptions: {
        column: {
          color: colors.primary,
          borderRadius: 4,
          pointPadding: 0.1,
          groupPadding: 0,
          borderWidth: 0,
          states: { hover: { color: colors.primaryDark } },
          dataLabels: {
            enabled: true,
            formatter() {
              return this.y >= 1000 ? (this.y / 1000).toFixed(1) + "k" : this.y;
            },
            style: {
              fontSize: "10px",
              fontWeight: "600",
              color: colors.textDark,
              textOutline: "none",
            },
          },
        },
      },
      series: [{ type: "column", name: "Providers", data }],
      tooltip: {
        ...tooltipBase,
        headerFormat: `<span style="font-size:14px;font-weight:600;color:#1E293B;">Intersection Size</span><br/><span style="color:#E96926;">─────</span><br/>`,
        pointFormatter() {
          const inter = intersections[this.index];
          if (!inter) return "";
          const setNames = inter.sets.map((s) => SETS[s]).join(" + ");
          const pct = ((inter.count / maxCount) * 100).toFixed(1);
          return `
            <div style="color:#64748B;font-size:11px;">INTERSECTION</div>
            <div style="font-size:16px;font-weight:700;color:#1E293B;">${setNames}</div>
            <div style="margin-top:10px;color:#64748B;font-size:11px;">PROVIDER COUNT</div>
            <div style="font-size:15px;font-weight:600;color:#1E293B;">${Highcharts.numberFormat(inter.count, 0, ".", ",")} Providers</div>
            <div style="margin-top:10px;color:#64748B;font-size:11px;">RELATIVE SIZE</div>
            <div style="font-size:14px;font-weight:600;color:#334155;">${pct}% of largest intersection</div>`;
        },
      },
      exporting: { enabled: false },
      legend: { enabled: false },
      credits: { enabled: false },
    });

    return () => chart.destroy();
  }, [intersections, chartW, maxCount]);

  // ── Set size bar chart ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!setBarRef.current) return;

    const chart = Highcharts.chart(setBarRef.current, {
      chart: {
        type: "bar",
        height: matrixH + 26,
        backgroundColor: colors.white,
        style: { fontFamily: "'Inter', sans-serif" },
        margin: [0, 52, 26, 10],
        spacing: [0, 0, 0, 0],
        animation: { duration: 400 },
      },
      title: { text: null },
      xAxis: {
        categories: SETS,
        labels: { enabled: false },
        lineWidth: 0,
        tickWidth: 0,
      },
      yAxis: {
        title: { text: null },
        tickAmount: 4, // 4 ticks max — prevents crowding
        labels: {
          style: {
            fontSize: "9px",
            color: colors.textMuted,
            fontFamily: "'Inter', sans-serif",
          },
          formatter() {
            return this.value >= 1000
              ? (this.value / 1000).toFixed(0) + "k"
              : this.value;
          },
        },
        gridLineColor: colors.grid,
        gridLineWidth: 1,
        min: 0,
      },
      plotOptions: {
        bar: {
          colorByPoint: true,
          colors: SET_COLORS,
          borderRadius: 4,
          borderWidth: 0,
          states: { hover: { brightness: -0.1 } },
          dataLabels: {
            enabled: true,
            formatter() {
              return this.y >= 1000 ? (this.y / 1000).toFixed(1) + "k" : this.y;
            },
            style: {
              fontSize: "10px",
              fontWeight: "600",
              color: colors.textDark,
              textOutline: "none",
            },
            align: "right",
            inside: true,
          },
        },
      },
      series: [{ name: "Set Size", data: SET_TOTALS }],
      tooltip: {
        ...tooltipBase,
        headerFormat: `<span style="font-size:14px;font-weight:600;color:#1E293B;">Payer Set Size</span><br/><span style="color:#E96926;">─────</span><br/>`,
        pointFormatter() {
          const setName = SETS[this.index];
          const total = SET_TOTALS.reduce((a, b) => a + b, 0);
          const pct = ((this.y / total) * 100).toFixed(1);
          return `
            <div style="color:#64748B;font-size:11px;">PAYER CATEGORY</div>
            <div style="font-size:16px;font-weight:700;color:#1E293B;">${setName}</div>
            <div style="margin-top:10px;color:#64748B;font-size:11px;">PROVIDER COUNT</div>
            <div style="font-size:15px;font-weight:600;color:#1E293B;">${Highcharts.numberFormat(this.y, 0, ".", ",")} Providers</div>
            <div style="margin-top:10px;color:#64748B;font-size:11px;">SHARE OF TOTAL</div>
            <div style="font-size:14px;font-weight:600;color:#334155;">${pct}% of all providers</div>`;
        },
      },
      exporting: { enabled: false },
      legend: { enabled: false },
      credits: { enabled: false },
    });

    return () => chart.destroy();
  }, [matrixH]);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Container
      fluid
      className="px-4 py-3"
      style={{ backgroundColor: colors.background, minHeight: "100vh" }}
    >
      <Card className="border-0 shadow-sm">
        <Card.Body className="p-3">
          <div className="d-flex align-items-stretch">
            {/* Left: spacer + set-size title + bars */}
            <div style={{ width: LEFT_W, flexShrink: 0 }}>
              <div style={{ height: BAR_H - 21 }} />
              <div style={{ paddingLeft: 8, paddingBottom: 4 }}>
                <div
                  style={{
                    fontSize: "13px",
                    fontWeight: "700",
                    color: "#2D2D4E",
                    fontFamily: "'Inter', sans-serif",
                  }}
                >
                  Set Size
                </div>
                <div
                  style={{
                    fontSize: "11px",
                    color: "#8B83A6",
                    fontFamily: "'Inter', sans-serif",
                  }}
                >
                  Total providers per payer
                </div>
              </div>
              <div ref={setBarRef} style={{ width: "100%" }} />
            </div>

            {/* Right: scrollable bar chart + dot matrix */}
            <div style={{ flex: 1, overflowX: "auto" }}>
              <div ref={barChartRef} style={{ width: chartW }} />

              <div
                style={{
                  height: 1,
                  backgroundColor: colors.border,
                  margin: "8px 0",
                }}
              />

              {/* Dot matrix — same width as bar chart so columns align */}
              <svg
                width={chartW}
                height={matrixH + LABEL_H}
                style={{ display: "block" }}
              >
                {/* Column hover highlight */}
                {intersections.map((_, ci) => (
                  <rect
                    key={`bg-${ci}`}
                    x={ci * COL_W + COL_W / 2 - DOT_R - 7}
                    y={0}
                    width={DOT_R * 2 + 14}
                    height={matrixH}
                    fill={hoveredCol === ci ? "#EEECF5" : "transparent"}
                    rx={6}
                    style={{ transition: "fill 0.15s" }}
                  />
                ))}

                {/* Connector lines */}
                {intersections.map((inter, ci) => {
                  if (inter.sets.length <= 1) return null;
                  const sorted = inter.sets.slice().sort();
                  const cx = ci * COL_W + COL_W / 2;
                  return (
                    <line
                      key={`line-${ci}`}
                      x1={cx}
                      y1={sorted[0] * ROW_H + ROW_H / 2}
                      x2={cx}
                      y2={sorted[sorted.length - 1] * ROW_H + ROW_H / 2}
                      stroke={
                        hoveredCol === ci ? colors.primaryDark : colors.line
                      }
                      strokeWidth={3}
                      strokeLinecap="round"
                      opacity={0.8}
                      style={{ transition: "stroke 0.15s" }}
                    />
                  );
                })}

                {/* Dots */}
                {SETS.map((_, si) =>
                  intersections.map((inter, ci) => {
                    const active = inter.sets.includes(si);
                    return (
                      <circle
                        key={`dot-${si}-${ci}`}
                        cx={ci * COL_W + COL_W / 2}
                        cy={si * ROW_H + ROW_H / 2}
                        r={DOT_R}
                        fill={
                          active
                            ? hoveredCol === ci
                              ? colors.primaryDark
                              : SET_COLORS[si]
                            : colors.dotEmpty
                        }
                        style={{ cursor: "default", transition: "fill 0.15s" }}
                        onMouseEnter={() => setHoveredCol(ci)}
                        onMouseLeave={() => setHoveredCol(null)}
                      />
                    );
                  }),
                )}
              </svg>
            </div>
          </div>

          {/* Legend */}
          <div
            className="d-flex flex-wrap justify-content-center align-items-center gap-3 mt-3"
            style={{ borderTop: `1px solid ${colors.border}`, paddingTop: 10 }}
          >
            {SETS.map((name, i) => (
              <div
                key={name}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: "12px",
                  color: colors.textDark,
                }}
              >
                <svg width={12} height={12}>
                  <circle cx={6} cy={6} r={6} fill={SET_COLORS[i]} />
                </svg>
                {name}
              </div>
            ))}
            <div
              style={{
                width: "100%",
                borderTop: `1px dashed ${colors.border}`,
                margin: "4px 0",
              }}
            />
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: "11px",
                color: colors.textMuted,
              }}
            >
              <svg width={12} height={12}>
                <circle cx={6} cy={6} r={6} fill={colors.dotEmpty} />
              </svg>
              Not in set
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: "11px",
                color: colors.textMuted,
              }}
            >
              <svg width={4} height={14}>
                <line
                  x1={2}
                  y1={0}
                  x2={2}
                  y2={14}
                  stroke={colors.line}
                  strokeWidth={3}
                />
              </svg>
              Connected sets
            </div>
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default UpSetPlot;
